import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MobAccountPageShell } from './MobAccountPageShell';
import { fetchCoupons, formatCouponMinOrder } from '../../utils/couponsApi';
import { useToast } from '../../Contexts/ToastContext';

function CouponDiscountStub({ coupon }) {
    if (coupon.type === 'percentage') {
        return (
            <>
                <span className="mob-coupon-ticket__amount">{coupon.discount}%</span>
                <span className="mob-coupon-ticket__amount-label">off</span>
            </>
        );
    }
    return (
        <>
            <span className="mob-coupon-ticket__amount mob-coupon-ticket__amount--flat">
                ₹{coupon.discount}
            </span>
            <span className="mob-coupon-ticket__amount-label">off</span>
        </>
    );
}

function CouponSkeleton() {
    return (
        <div className="mob-coupon-ticket mob-coupon-ticket--skeleton" aria-hidden>
            <div className="mob-coupon-ticket__stub" />
            <div className="mob-coupon-ticket__panel">
                <div className="mob-coupon-ticket__sk-line mob-coupon-ticket__sk-line--lg" />
                <div className="mob-coupon-ticket__sk-line" />
                <div className="mob-coupon-ticket__sk-line mob-coupon-ticket__sk-line--sm" />
            </div>
        </div>
    );
}

export function MobCoupons() {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [tab, setTab] = useState('active');
    const [catalog, setCatalog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const list = await fetchCoupons();
                if (!cancelled) setCatalog(list);
            } catch (_e) {
                if (!cancelled) {
                    setError('Could not load coupons. Please try again.');
                    setCatalog([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const activeCoupons = useMemo(() => catalog.filter((c) => !c.expired), [catalog]);
    const expiredCoupons = useMemo(() => catalog.filter((c) => c.expired), [catalog]);
    const coupons = tab === 'active' ? activeCoupons : expiredCoupons;
    const showCartCta = !loading && activeCoupons.length > 0;

    const handleCopy = async (code) => {
        try {
            await navigator.clipboard.writeText(code);
            showToast({ message: `Coupon ${code} copied`, variant: 'success' });
        } catch (_e) {
            showToast({ message: code, variant: 'success' });
        }
    };

    return (
        <MobAccountPageShell title="Coupons">
            <div className="mob-coupons-page">
                <header className="mob-coupons-hero">
                    <div className="mob-coupons-hero__icon" aria-hidden>
                        <i className="bi bi-ticket-perforated-fill" />
                    </div>
                    <div className="mob-coupons-hero__text">
                        <h1 className="mob-coupons-hero__title">Your coupons</h1>
                        <p className="mob-coupons-hero__sub">
                            {loading
                                ? 'Loading offers…'
                                : activeCoupons.length > 0
                                  ? `${activeCoupons.length} ready to use at checkout`
                                  : 'Check back for new offers'}
                        </p>
                    </div>
                </header>

                <p className="mob-coupons-hint">
                    <i className="bi bi-info-circle" aria-hidden />
                    Apply on cart or order summary before you pay.
                </p>

                <div className="mob-coupons-tabs" role="tablist" aria-label="Coupon filters">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={tab === 'active'}
                        className={`mob-coupons-tabs__btn${tab === 'active' ? ' mob-coupons-tabs__btn--active' : ''}`}
                        onClick={() => setTab('active')}
                    >
                        Available
                        {!loading && (
                            <span className="mob-coupons-tabs__count">{activeCoupons.length}</span>
                        )}
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={tab === 'expired'}
                        className={`mob-coupons-tabs__btn${tab === 'expired' ? ' mob-coupons-tabs__btn--active' : ''}`}
                        onClick={() => setTab('expired')}
                    >
                        Expired
                        {!loading && expiredCoupons.length > 0 && (
                            <span className="mob-coupons-tabs__count">{expiredCoupons.length}</span>
                        )}
                    </button>
                </div>

                {error && (
                    <div className="mob-coupons-alert" role="alert">
                        <i className="bi bi-exclamation-circle" aria-hidden />
                        <span>{error}</span>
                    </div>
                )}

                <div
                    className={`mob-coupons-sticky-wrap${
                        showCartCta ? ' mob-coupons-sticky-wrap--active' : ''
                    }`}
                >
                <div className="mob-coupons-list">
                    {loading ? (
                        <>
                            <CouponSkeleton />
                            <CouponSkeleton />
                            <CouponSkeleton />
                        </>
                    ) : coupons.length === 0 ? (
                        <div className="mob-coupons-empty">
                            <div className="mob-coupons-empty__icon" aria-hidden>
                                <i className="bi bi-tag" />
                            </div>
                            <p className="mob-coupons-empty__title">
                                No {tab === 'active' ? 'available' : 'expired'} coupons
                            </p>
                            <p className="mob-coupons-empty__sub">
                                {tab === 'active'
                                    ? 'New offers will show up here when they’re live.'
                                    : 'Expired codes are kept here for reference.'}
                            </p>
                        </div>
                    ) : (
                        coupons.map((coupon) => (
                            <article
                                key={coupon.id}
                                className={`mob-coupon-ticket${coupon.expired ? ' mob-coupon-ticket--expired' : ''}`}
                            >
                                {coupon.expired && (
                                    <span className="mob-coupon-ticket__badge">Expired</span>
                                )}
                                <div className="mob-coupon-ticket__stub">
                                    <CouponDiscountStub coupon={coupon} />
                                </div>
                                <div className="mob-coupon-ticket__panel">
                                    <div className="mob-coupon-ticket__code-row">
                                        <span className="mob-coupon-ticket__code">{coupon.code}</span>
                                        {!coupon.expired && (
                                            <button
                                                type="button"
                                                className="mob-coupon-ticket__copy-icon"
                                                onClick={() => handleCopy(coupon.code)}
                                                aria-label={`Copy coupon ${coupon.code}`}
                                            >
                                                <i className="bi bi-copy" aria-hidden />
                                            </button>
                                        )}
                                    </div>
                                    <p className="mob-coupon-ticket__desc">{coupon.description}</p>
                                    <ul className="mob-coupon-ticket__meta">
                                        <li>
                                            <i className="bi bi-cart3" aria-hidden />
                                            {formatCouponMinOrder(coupon.minOrder)}
                                        </li>
                                        <li>
                                            <i className="bi bi-calendar3" aria-hidden />
                                            Valid till {coupon.validUntil}
                                        </li>
                                    </ul>
                                </div>
                            </article>
                        ))
                    )}
                </div>

                {showCartCta && (
                    <footer className="mob-coupons-footer">
                        <button
                            type="button"
                            className="mob-coupons-footer__cta"
                            onClick={() => navigate('/cart')}
                        >
                            Go to cart
                        </button>
                    </footer>
                )}
                </div>
            </div>
        </MobAccountPageShell>
    );
}
